/*
 * Nether: in-place gallery zoom — replaces Horizon's full-screen zoom dialog.
 *
 * WHY THIS EXISTS. Horizon's zoom opens <zoom-dialog>, a full-viewport takeover
 * with its own thumbnail rail, and the only way out is the X in the corner.
 * Owner, 2026-09-08, after watching Clarity replays: "you can hit the X to
 * close, but double-tapping the gallery or something like that doesn't get it
 * out... this zoomed-in gallery just feels lame all the way through."
 *
 * The trap is a STOCK HORIZON BUG, and worth recording precisely because it is
 * the reason this file exists rather than a patch. assets/drag-zoom-wrapper.js
 * line 109 calls preventDefault() on EVERY touchstart, registered
 * { passive: false }. On iOS, preventDefault on touchstart suppresses the
 * synthesized click entirely — so the `on:click="/close"` that Horizon puts on
 * the dialog's <li> never receives anything. The handler is there and it works;
 * the gesture never reaches it. The X sits outside the wrapper, which is why it
 * is the only exit. Every Horizon store with gallery zoom on has this.
 *
 * THE RULE THAT KEEPS US OUT OF THE SAME HOLE: this file never calls
 * preventDefault, anywhere, for any reason. It listens for `click` — the event
 * the browser only produces when nobody has cancelled the touch — so a tap, a
 * swipe and a pinch all keep behaving exactly as they do with no zoom at all.
 * Native pinch-to-zoom still works over the top of it; the viewport is
 * width=device-width,initial-scale=1 with no maximum-scale.
 *
 * Panning while zoomed is deliberately NOT implemented. It would require
 * preventDefault on touchmove, which is one guard away from being the bug above,
 * and it is not needed: the zoom is anchored to the point you touched, so the
 * detail you reached for is already under your finger.
 *
 * Mutually exclusive with the stock dialog by construction — the gallery snippet
 * loads either zoom-dialog.js or this, never both, off block_settings.zoom.
 * Delegated at the document so it survives Horizon's section morph on variant
 * change, which replaces the gallery nodes (same reason
 * nether-gallery-progress.js listens at the document).
 */
(function () {
  'use strict';

  /* Enough to read a seam or the waistband weave, not so much that the image
     turns to mush. The product photography is 1400px wide against a slide about
     375 CSS px, so there is real detail to uncover here. */
  var SCALE = 2.2;

  /** @type {Element|null} The container currently zoomed, or null. */
  var zoomed = null;

  function imageOf(container) {
    return container ? container.querySelector('.product-media__image') : null;
  }

  function reset() {
    if (!zoomed) return;
    var img = imageOf(zoomed);
    if (img) {
      /* Transform only — the ORIGIN IS DELIBERATELY LEFT SET (owner 2026-09-08:
         "when you click to get out of the zoom, it snaps to the original view
         instead of just zooming out from wherever you were").

         Clearing both in the same frame was the bug. transform-origin has no
         transition, so dropping it back to the 50% 50% default takes effect
         instantly while the scale is still animating down — the image shrinks
         AND slides to centre at once, which reads as a snap.

         Leaving the origin where it is costs nothing: at scale 1 the origin is
         invisible, and the next zoomAt() overwrites it before it matters again.
         So the photo now shrinks back toward the point you were looking at,
         which is the gesture reversing rather than the view resetting. */
      img.style.transform = '';
    }
    zoomed.removeAttribute('data-nether-zoomed');
    document.documentElement.removeAttribute('data-nether-gallery-zoomed');
    zoomed = null;
  }

  function zoomAt(container, clientX, clientY) {
    var img = imageOf(container);
    if (!img) return;

    var r = img.getBoundingClientRect();
    if (!r.width || !r.height) return;

    /* Anchor the scale on the point that was touched, as a percentage, so the
       pixel under the finger stays under the finger. Clamped because a tap can
       land a hair outside the box on a fast scroll. */
    var ox = Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100));
    var oy = Math.max(0, Math.min(100, ((clientY - r.top) / r.height) * 100));

    img.style.transformOrigin = ox.toFixed(2) + '% ' + oy.toFixed(2) + '%';
    img.style.transform = 'scale(' + SCALE + ')';
    container.setAttribute('data-nether-zoomed', '');
    document.documentElement.setAttribute('data-nether-gallery-zoomed', '');
    zoomed = container;
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    /* Controls win. The pager, the arrows and any card link inside the gallery
       keep their own behaviour — only bare media responds to a tap. */
    if (target.closest('button, a, summary, input, label')) return;

    var container = target.closest('.product-media-container');
    if (!container || !container.closest('media-gallery')) return;

    /* Video and 3D model slides have their own controls; scaling their poster
       would fight the play button. */
    if (!imageOf(container) || container.querySelector('video, product-model')) return;

    if (zoomed === container) {
      reset();
      return;
    }
    reset();
    zoomAt(container, event.clientX, event.clientY);
  });

  /* Swiping to another photo drops the zoom — staying zoomed while the carousel
     moves under it looks broken. scroll does not bubble, so this captures. */
  document.addEventListener(
    'scroll',
    function (event) {
      if (!zoomed) return;
      var t = event.target;
      if (t && t.closest && t.closest('media-gallery')) reset();
    },
    { capture: true, passive: true }
  );

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') reset();
  });

  window.addEventListener('resize', reset, { passive: true });

  /* A colourway change morphs the section, so the node we are holding can be
     replaced out from under us. Dropping the stale reference stops the next tap
     from trying to clear inline styles on a detached element. */
  document.addEventListener('variant:selected', function () {
    if (zoomed && !document.contains(zoomed)) {
      document.documentElement.removeAttribute('data-nether-gallery-zoomed');
      zoomed = null;
      return;
    }
    reset();
  });
})();
